/** Features — LOTE M. URL: /features */
import { useEffect, useState } from 'react';
import { Shirt, Camera, Sparkles, Fingerprint, CalendarDays, CloudSun, Plane, ShoppingBag } from 'lucide-react';
import { useWww } from './_layout';
import { wwwTheme, wwwRadius, wwwFonts } from '@/src/website/theme';
import { SeoHead } from '@/src/website/SeoHead';
import { SHOP_PUBLIC } from '@/src/website/launch';

// Web equivalent of RN's useWindowDimensions().
function useWindowDimensions() {
  const [size, setSize] = useState(() => ({
    width: typeof window !== 'undefined' ? window.innerWidth : 1024,
    height: typeof window !== 'undefined' ? window.innerHeight : 768,
  }));
  useEffect(() => {
    const onResize = () => setSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return size;
}

export default function Features() {
  const { t, lang } = useWww();
  const { width } = useWindowDimensions();
  const isMobile = width < 760;
  const groups = [
    { icon: Shirt,        t: t.featuresGroup1Title, b: t.featuresGroup1Body },
    { icon: Camera,       t: t.featuresGroup2Title, b: t.featuresGroup2Body },
    { icon: Sparkles,     t: t.featuresGroup3Title, b: t.featuresGroup3Body },
    { icon: Fingerprint,  t: t.featuresProfileTitle, b: t.featuresProfileBody },
    { icon: CalendarDays, t: t.featuresGroup4Title, b: t.featuresGroup4Body },
    { icon: CloudSun,     t: t.featuresGroup5Title, b: t.featuresGroup5Body },
    { icon: Plane,        t: t.featuresTravelTitle, b: t.featuresTravelBody, id: 'travel' },
    { icon: ShoppingBag,  t: t.shopTitle, b: SHOP_PUBLIC ? t.shopIntroPublic : t.shopIntroBeta, id: 'shop' },
  ];

  // Scroll to #travel (or any hash) when arriving from the home Travel CTA.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.location.hash) return;
    const el = document.getElementById(window.location.hash.slice(1));
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [lang]);

  // Web replacement for <Stack.Screen options={{ title: ... }} />: update
  // document.title so the browser tab matches the string the native screen
  // header used in the Expo version. Runs after SeoHead's effect so it has
  // the final word, exactly as in the original render order.
  useEffect(() => {
    document.title = t.featuresTitle + ' · STILGIA';
  }, [t.featuresTitle]);

  return (
    <>
      <SeoHead page="features" locale={lang} />

      <div style={{ paddingLeft: isMobile ? 24 : 48, paddingRight: isMobile ? 24 : 48, paddingTop: 64, paddingBottom: 32, maxWidth: 980, width: '100%', marginLeft: 'auto', marginRight: 'auto', boxSizing: 'border-box' }}>
        <h1 style={{ color: wwwTheme.text, fontFamily: wwwFonts.serifBold, fontSize: isMobile ? 34 : 50, lineHeight: isMobile ? '40px' : '56px', marginTop: 0, marginBottom: 0 }}>{t.featuresTitle}</h1>
        <p style={{ color: wwwTheme.muted, fontSize: isMobile ? 16 : 18, lineHeight: '26px', marginTop: 16, marginBottom: 0, maxWidth: 720 }}>{t.featuresSubtitle}</p>
      </div>

      <div style={{ paddingLeft: isMobile ? 24 : 48, paddingRight: isMobile ? 24 : 48, paddingBottom: 64, maxWidth: 980, width: '100%', marginLeft: 'auto', marginRight: 'auto', boxSizing: 'border-box' }}>
        {groups.map((g, i) => {
          const Icon = g.icon;
          return (
            <div key={i} id={(g as any).id} data-testid={`features-group-${i + 1}`} style={{ paddingTop: 32, paddingBottom: 32, borderBottomColor: wwwTheme.border, borderBottomWidth: i === groups.length - 1 ? 0 : 1, borderBottomStyle: 'solid', scrollMarginTop: 24 }}>
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                <div style={{ width: 40, height: 40, borderRadius: wwwRadius.md, borderColor: wwwTheme.border, borderWidth: 1, borderStyle: 'solid', display: 'flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box', flexShrink: 0 }}>
                  <Icon size={19} color={wwwTheme.text} strokeWidth={1.6} />
                </div>
                <span style={{ color: wwwTheme.muted, fontSize: 12, fontWeight: 600, letterSpacing: '1.5px' }}>{String(i + 1).padStart(2, '0')}</span>
              </div>
              <h2 style={{ color: wwwTheme.text, fontSize: isMobile ? 24 : 28, fontWeight: 600, marginTop: 0, marginBottom: 12, fontFamily: wwwFonts.serifBold }}>{g.t}</h2>
              <p style={{ color: wwwTheme.muted, fontSize: 15, lineHeight: '24px', marginTop: 0, marginBottom: 0 }}>{g.b}</p>
            </div>
          );
        })}
      </div>
    </>
  );
}
